import { STAY_UNTIL_CLOSED } from "Reducers/notifications";
import { notifyWithPriorityHandler as notify } from "src/common/containers/Notifications/util";

import { getAgentRunUrl } from "../getAgentRunUrl";

import type { IntlShape } from "react-intl";
import type { AgentRun } from "Reducers/agentRuns";
import type { AppDispatch } from "Store/configureStore";

interface NotifyOptions {
  run: AgentRun;
  dispatch: AppDispatch;
  intl: IntlShape;
}

// Every toast opens the run (`getAgentRunUrl`) and stays until closed (design
// review): a run takes minutes, and a toast that hides after 7 seconds is gone
// before anyone looks for it.

/**
 * The started toast's id comes from the run, so the completed and failed
 * toasts can remove it without the run having to store it.
 */
const getStartedToastId = (run: AgentRun) =>
  `log-analyzer-started-${run.activityId}`;

/**
 * "The agent is running, you can keep working." Fired once the run is
 * accepted; the Logs page stays where it is.
 *
 * The AI sparkle, as in the design. The completed toast keeps the success
 * check.
 */
export const notifyLogAnalyzerStarted = ({
  run,
  dispatch,
  intl
}: NotifyOptions) =>
  notify(dispatch).information({
    id: getStartedToastId(run),
    title: intl.formatMessage({ id: "log_analyzer_agent.toast.started.title" }),
    description: intl.formatMessage(
      { id: "log_analyzer_agent.toast.started.description" },
      { environmentName: run.environmentName }
    ),
    timeout: STAY_UNTIL_CLOSED,
    icon: "sparkles",
    link: {
      title: intl.formatMessage({
        id: "log_analyzer_agent.toast.started.link"
      }),
      location: { to: getAgentRunUrl(run) },
      closeOnClick: true
    }
  });

/**
 * Removes the run's started toast. The completed and failed toasts call it,
 * and so does a cancelled run, which would otherwise say "started" after it is
 * over.
 */
export const removeLogAnalyzerStartedToast = ({
  run,
  dispatch
}: Pick<NotifyOptions, "run" | "dispatch">) =>
  notify(dispatch).remove(getStartedToastId(run));

/**
 * "Your report is ready." Removes the run's started toast first, so the user
 * never sees both for one run. Toasts from other runs stay.
 */
export const notifyLogAnalyzerCompleted = ({
  run,
  dispatch,
  intl
}: NotifyOptions) => {
  removeLogAnalyzerStartedToast({ run, dispatch });

  return notify(dispatch).success({
    title: intl.formatMessage({
      id: "log_analyzer_agent.toast.completed.title"
    }),
    description: intl.formatMessage(
      { id: "log_analyzer_agent.toast.completed.description" },
      { environmentName: run.environmentName }
    ),
    timeout: STAY_UNTIL_CLOSED,
    link: {
      title: intl.formatMessage({
        id: "log_analyzer_agent.toast.completed.link"
      }),
      location: { to: getAgentRunUrl(run) },
      closeOnClick: true
    }
  });
};

/**
 * "The run is over and there is no report." The container exited on its own
 * before the agent printed one. A cancelled run gets no toast: the user
 * stopped it themselves. Replaces the run's started toast.
 */
export const notifyLogAnalyzerFailed = ({
  run,
  dispatch,
  intl
}: NotifyOptions) => {
  removeLogAnalyzerStartedToast({ run, dispatch });

  return notify(dispatch).error({
    title: intl.formatMessage({ id: "log_analyzer_agent.toast.failed.title" }),
    description: intl.formatMessage({
      id: "log_analyzer_agent.toast.failed.description"
    }),
    timeout: STAY_UNTIL_CLOSED,
    link: {
      title: intl.formatMessage({ id: "log_analyzer_agent.toast.failed.link" }),
      location: { to: getAgentRunUrl(run) },
      closeOnClick: true
    }
  });
};
